<template>
    <div>
        <meta name="csrf-token" :content="csrfToken" />

        <!-- Page Content -->
        <div class="content">
            <div v-if="errorMessages.length" class="alert alert-danger">
                <ul>
                    <li v-for="(error, index) in errorMessages" :key="index">{{ error }}</li>
                </ul>
            </div>

            <div class="row mb-2 text-end">
                <div class="d-flex gap-1 justify-content-end align-items-center mb-2 flex-wrap">
                    <template v-if="!shipment.deleted_at">
                        <!-- Buttons with permissions -->
                        <button v-if="hasPermission('permission_users_module_admin')" type="button"
                            class="btn btn-sm btn-outline-secondary" @click="printFullTag">
                            <i class="fa fa-fw fa-print me-1"></i>
                            {{ $t('button_print_waybill') }}
                        </button>
                        <button v-if="hasPermission('permission_users_module_admin')" type="button"
                            class="btn btn-sm btn-outline-secondary print-barcode" :data-shipment-id="shipment.id">
                            <i class="fa fa-fw fa-barcode me-1"></i>
                            {{ $t('button_print_barcode') }}
                        </button>
                        <!-- Change Status Modal Button -->
                        <div v-if="hasPermission('permission_shipments_module_admin')">
                            <ChangeStatusModal ref="changeStatusModal" :changeable-shipments-statuses="shipmentStatus"
                                :grouped_users="grouped_users" :selected-shipments="[shipment._id]"
                                @status-updated="handleStatusUpdate"></ChangeStatusModal>
                        </div>
                        <div v-if="hasPermission('permission_shipments_module_admin')">
                            <ChangeCustodyModal ref="changeCustodyModal" :grouped_users="grouped_users"
                                :selected-shipments="[shipment]" @custody-updated="handleCustodyUpdate">
                            </ChangeCustodyModal>
                        </div>
                        <a v-if="hasPermission('permission_users_module_admin')"
                            :href="`/shipments/update/${shipment._id}`" class="btn btn-sm btn-outline-secondary">
                            <i class="fa fa-fw fa-pen-to-square me-1"></i>
                            {{ $t('button_edit') }}
                        </a>
                        <!-- Delete Button -->
                        <button v-if="hasPermission('permission_users_module_admin')" type="button"
                            class="btn btn-sm btn-outline-danger" @click="deleteShipment" id="deleteButton">
                            <i class="fa fa-fw fa-trash-can me-1"></i> {{ $t('button_delete') }}
                        </button>
                    </template>
                    <template v-else>
                        <button v-if="hasPermission('permission_users_module_admin')" type="button"
                            class="btn btn-sm btn-outline-secondary" @click="restoreShipment">
                            <i class="fa fa-fw fa-trash-arrow-up me-1"></i>
                            {{ $t('button_restore') }}
                        </button>
                    </template>
                </div>
            </div>

            <!-- Tabs -->
            <div class="block-rounded block">
                <ul class="nav nav-tabs nav-tabs-alt align-items-center" role="tablist">
                    <li v-if="hasPermission('permission_users_module_user')" class="nav-item" role="presentation">
                        <button class="nav-link active" data-bs-toggle="tab" data-bs-target="#ecom-product-info">
                            {{ $t('additional_info') }}
                        </button>
                    </li>
                    <li v-if="hasPermission('permission_users_module_admin')" class="nav-item" role="presentation">
                        <button type="button" class="nav-link" id="shipment-feedback-data-tab" data-bs-toggle="tab"
                            data-bs-target="#shipment_feedback" role="tab" aria-controls="ecom-product-reviews"
                            aria-selected="false">
                            {{ $t('feedback') }}
                            <div v-if="feedbackTapLoading" class="spinner-border spinner-border-sm text-primary"
                                role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                            <span v-if="!feedbackTapLoading" id="feedbackCount">({{ feedbacks.length }})</span>
                        </button>
                    </li>

                    <!-- History Tab Button -->
                    <li v-if="hasPermission('permission_users_module_admin')" class="nav-item" role="presentation">
                        <button type="button" class="nav-link" id="audit-log-tab" data-bs-toggle="tab"
                            data-bs-target="#ecom-product-comments" role="tab" aria-controls="ecom-product-reviews"
                            aria-selected="false" tabindex="-1">
                            {{ $t('history') }}
                            <div v-if="logTapLoading" class="spinner-border spinner-border-sm text-primary"
                                role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                            <span v-if="!logTapLoading" id="historyCount">({{ historyCount }})</span>
                        </button>
                    </li>
                    <!-- Other tabs... -->
                </ul>

                <div class="block-content tab-content">
                    <!-- Shipment Info Tab -->
                    <div v-if="hasPermission('permission_users_module_user')" class="tab-pane pull-x active"
                        id="ecom-product-info">
                        <table class="table-striped table-borderless table-to-print table">
                            <thead>
                                <tr>
                                    <th colspan="2">{{ $t('essintial_info') }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td style="width: 30%;"><i class="fa fa-fw fa-hashtag text-muted me-1"></i>
                                        {{ $t('input_reference') }}</td>
                                    <td id="RefResult">
                                        {{ shipment.reference }}
                                    </td>
                                </tr>
                                <template>
                                    <tr>
                                    <td style="width: 30%;"><i class="fa fa-fw fa-hashtag text-muted me-1"></i>
                                        {{ $t('input_foreign_reference') }}</td>
                                    <td id="RefResult">
                                        {{ shipment.foreign_reference }}
                                    </td>
                                </tr>
                                </template>
                                <tr>
                                    <td style="vertical-align: middle;">
                                        <i class="fa fa-fw fa-user text-muted me-1"></i>
                                        {{ $t('input_client') }}
                                    </td>
                                    <td>
                                        <template v-if="shipment.client_full_data">
                                            <single-avatar-chip :id="shipment.client_full_data.id"
                                                :name="`${shipment.client_full_data.first_name} ${shipment.client_full_data.last_name}`"
                                                :avatar="shipment.client_full_data.avatar">
                                            </single-avatar-chip>
                                        </template>
                                    </td>

                                </tr>
                                <tr>
                                    <td style="width: 30%;">
                                        <i class="fa fa-fw fa-signal text-muted me-1"></i> {{ $t('input_status') }}
                                    </td>
                                    <td id="StatusResult">
                                        <!-- Check if shipment is trashed -->
                                        <span v-if="shipment.trashed">
                                            <span class="badge bg-danger fw-bold text-uppercase p-2">
                                                {{ $t('Deleted') }}
                                            </span>
                                        </span>
                                        <span v-else>
                                            <span class="badge"
                                                :class="'bg-' + shipment.status_data.color + ' fw-bold text-uppercase p-2'">
                                                {{ shipment.status_data.title_ar }}
                                            </span>
                                        </span>
                                    </td>
                                </tr>
                                <tr>
                                    <td><i class="fa fa-fw fa-pen text-muted me-1"></i>
                                        {{ $t('input_name') }}
                                    </td>
                                    <td>
                                        <span class="text-break">{{ shipment.receiver_name }}</span>
                                    </td>
                                </tr>
                                <tr>
                                    <td style="width: 20%; vertical-align: middle;">
                                        <i class="fa fa-fw fa-phone text-muted me-1"></i> {{ $t('input_mobile_number')
                                        }}
                                    </td>
                                    <td id="client_sim_number_result">
                                        <template
                                            v-if="Array.isArray(shipment.receiver_sim_number) && shipment.receiver_sim_number.length > 0">
                                            <div v-for="(number, index) in shipment.receiver_sim_number" :key="index"
                                                style="margin-bottom: 5px; display: flex; align-items: center;">
                                                <span>{{ number }}</span>
                                                <a :href="'https://api.whatsapp.com/send?phone=' + number + '&text=' + encodeURIComponent('Hello')"
                                                    target="_blank" class="btn btn-success btn-sm ms-2"
                                                    title="Chat on WhatsApp">
                                                    <i class="fa-brands fa-whatsapp"></i>
                                                </a>
                                                <a :href="'tel:' + number" class="btn btn-primary btn-sm ms-2"
                                                    title="Call">
                                                    <i class="fa fa-phone"></i>
                                                </a>
                                            </div>
                                        </template>
                                        <template v-else>
                                            <div>No SIM numbers available</div>
                                        </template>
                                    </td>

                                </tr>
                                <tr>
                                    <td style="width: 30%;"><i class="fa fa-fw fa-city text-muted me-1"></i>
                                        {{ $t('input_area') }}</td>
                                    <td id="AreaResult">
                                        <template v-if="shipment.area_data">
                                            {{ shipment.area_data.city_name_ar }} -
                                            {{
                                                shipment.area_data.governorate.governorate_name_ar }}
                                        </template>
                                        <template v-else>
                                            <span
                                                class="fs-xs fw-semibold d-inline-block rounded-pill bg-danger-light text-danger px-3 py-1">
                                                No Area
                                            </span>
                                        </template>
                                    </td>

                                </tr>
                                <tr>
                                    <td><i class="fa fa-fw fa-pen text-muted me-1"></i>
                                        {{ $t('input_shipment_address') }}
                                    </td>
                                    <td>
                                        <span class="text-break editable" contenteditable="false">{{
                                            shipment.receiver_address
                                            }}</span>
                                    </td>
                                </tr>
                                <template v-if="shipment.latitude && shipment.longitude">
                                <tr>
                                    <td>
                                        <i class="fa fa-fw fa-location-dot text-muted me-1"></i>
                                        {{ $t('input_maps_url') }}
                                    </td>
                                    <td>
                                            <a :href="`https://www.google.com/maps?q=${shipment.latitude},${shipment.longitude}`"
                                                target="_blank" class="btn btn-sm btn-alt-primary">
                                                <i class="fa-solid fa-map-location-dot me-1"></i>
                                                {{ $t('button_open_in_google_maps') }}
                                            </a>
                                        </td>
                                        
                                    </tr>
                                </template>
                                <tr>
                                    <td><i class="fa fa-fw fa-ruler-vertical text-muted me-1"></i>
                                        {{ $t('input_height') }}</td>
                                    <td id="AreaResult">
                                        {{ shipment.height }} cm
                                    </td>

                                </tr>
                                <tr>
                                    <td><i class="fa fa-fw fa-ruler-horizontal text-muted me-1"></i>
                                        {{ $t('input_width') }}</td>
                                    <td id="AreaResult">
                                        {{ shipment.width }} cm
                                    </td>

                                </tr>
                                <tr>
                                    <td><i class="fa fa-fw fa-solid fa-weight-scale text-muted me-1"></i>
                                        {{ $t('input_weight') }}</td>
                                    <td id="AreaResult">
                                        {{ shipment.weight }} kg
                                    </td>
                                </tr>
                                <tr>
                                    <td><i class="fa fa-fw fa-regular fa-note-sticky text-muted me-1"></i>
                                        {{ $t('input_notes') }}
                                    </td>
                                    <td>
                                        <span class="text-break">{{ shipment.notes }}</span>
                                    </td>
                                </tr>
                                <tr>
                                    <td><i class="fa fa-fw fa-box-open text-muted me-1"></i>
                                        {{ $t('input_shipment_price') }}</td>
                                    <td id="CommissionValueResult">
                                        {{ shipment.shipment_price }}


                                    </td>

                                </tr>
                                <tr>
                                    <td><i class="fa fa-fw fa-truck text-muted me-1"></i>
                                        {{ $t('input_delivery_price') }}</td>
                                    <td id="CommissionValueResult">
                                        {{ shipment.delivery_price }}


                                    </td>
                                </tr>
                                <tr>
                                    <td><i class="fa fa-fw fa-dollar-sign text-muted me-1"></i>
                                        {{ $t('input_grand_total') }}</td>
                                    <td id="TotalPriceResult">
                                        {{ shipment.grand_total }}


                                    </td>
                                </tr>
                                <tr>
                                    <td style="vertical-align: middle;">
                                        <i class="fa fa-fw fa-user text-muted me-1"></i>
                                        {{ $t('input_assigned_to') }}
                                    </td>
                                    <td>
                                        <template v-if="shipment.assigned_to_user">
                                            <single-avatar-chip :id="shipment.assigned_to_user._id"
                                                :name="`${shipment.assigned_to_user.first_name} ${shipment.assigned_to_user.last_name}`"
                                                :avatar="shipment.assigned_to_user.avatar">
                                            </single-avatar-chip>
                                        </template>
                                    </td>

                                </tr>

                            </tbody>
                        </table>
                        <table class="table-striped table-borderless table-to-print table">
                            <thead>
                                <tr>
                                    <th colspan="2">{{ $t('additional_info') }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <!-- Shipment ID (hidden) -->
                                <tr v-if="shipment.id">
                                    <td>
                                        <i class="fa fa-fw fa-hashtag text-muted me-1"></i>
                                        {{ $t('input_id') }}
                                    </td>
                                    <td id="shipment_id">{{ shipment.id }}</td>
                                </tr>

                                <!-- Last Updated At -->
                                <tr>
                                    <td style="width: 30%;">
                                        <i class="fa fa-fw fa-calendar text-muted me-1"></i>
                                        {{ $t('input_last_updated_at') }}
                                    </td>
                                    <td id="updated_at_result">{{ formatDate(shipment.updated_at) }}</td>
                                </tr>

                                <!-- Last Updated By -->
                                <tr>
                                    <td style="vertical-align: middle;">
                                        <i class="fa fa-fw fa-user text-muted me-1"></i>
                                        {{ $t('input_last_updated_by') }}
                                    </td>
                                    <td id="updated_by_result">
                                        <template v-if="shipment.last_updated_by_user">
                                            <single-avatar-chip :id="shipment.last_updated_by_user._id"
                                                :name="`${shipment.last_updated_by_user.first_name} ${shipment.last_updated_by_user.last_name}`"
                                                :avatar="shipment.last_updated_by_user.avatar">
                                            </single-avatar-chip>
                                        </template>
                                    </td>
                                </tr>

                                <!-- Created At -->
                                <tr>
                                    <td style="width: 30%;">
                                        <i class="fa fa-fw fa-calendar text-muted me-1"></i>
                                        {{ $t('input_created_at') }}
                                    </td>
                                    <td id="created_at_result">{{ formatDate(shipment.created_at) }}</td>
                                </tr>

                                <!-- Created By -->
                                <tr>
                                    <td style="vertical-align: middle;">
                                        <i class="fa fa-fw fa-user text-muted me-1"></i>
                                        {{ $t('input_created_by') }}
                                    </td>
                                    <td id="created_by_result">
                                        <template v-if="shipment.creator">
                                            <single-avatar-chip :id="shipment.creator._id"
                                                :name="`${shipment.creator.first_name} ${shipment.creator.last_name}`"
                                                :avatar="shipment.creator.avatar">
                                            </single-avatar-chip>
                                        </template>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div v-if="permissions.canManageShipment" class="tab-pane pull-x" id="shipment_feedback"
                        role="tabpanel" aria-labelledby="shipment-feedback-data-tab" tabindex="0">
                        <!-- Error Messages -->
                        <div v-if="initialErrors.length">
                            <div v-for="(error, index) in initialErrors" :key="index"
                                class="alert alert-danger alert-dismissible">
                                {{ error }}
                                <button type="button" class="btn-close" data-bs-dismiss="alert"
                                    aria-label="Close"></button>
                            </div>
                        </div>

                        <!-- Unauthorized Message -->
                        <span v-if="isUnauthorized" class="text-danger" id="FeedbackUnauth"> - Unauth, Please Contact
                            With Your
                            System Administrator</span>

                        <!-- Feedback Table -->
                        <table class="table-borderless table" id="feedback_table">
                            <tr v-if="!shipment.trashed">
                                <td class="d-none fs-sm fw-medium d-sm-table-cell text-center" style="width: 140px;">
                                    <p>
                                        <a :href="'be_pages_generic_profile.html'">
                                            <img class="img-avatar" :src="user.avatar" alt="">
                                        </a>
                                        {{ user.first_name }} {{ user.last_name }}<br><span class="text-muted">{{
                                            user.role.role_title
                                        }}</span>
                                    </p>
                                </td>
                                <td>
                                    <div class="mb-4">
                                        <!-- Unreachable Toggle -->
                                        <div class="form-check form-switch mb-2">
                                            <input class="form-check-input" type="checkbox" id="unreachable"
                                                v-model="unreachable" />
                                            <label class="form-check-label" for="unreachable">
                                                {{ $t('placeholder_is_unreachable') }}
                                            </label>
                                        </div>

                                        <!-- Evading Toggle -->
                                        <div class="form-check form-switch mb-3">
                                            <input class="form-check-input" type="checkbox" id="evading"
                                                v-model="evading" />
                                            <label class="form-check-label" for="evading">
                                                {{ $t('placeholder_is_evading') }}
                                            </label>
                                        </div>
                                        <textarea class="form-control" v-model="feedbackContent" id="feedback_content"
                                            name="feedback_content" rows="4"></textarea>
                                    </div>
                                    <div class="mb-4">
                                        <button type="button" id="postFeedback" class="btn btn-alt-primary"
                                            @click="postFeedback">
                                            <i class="fa fa-reply me-1 opacity-50"></i> {{ $t('button_submit') }}
                                        </button>
                                    </div>
                                </td>
                            </tr>
                            <tbody id="feedback_tbody">
                                <template v-for="(feedback, index) in feedbacks" :key="index">
                                    <tr class="bg-body-light">
                                        <td class="d-none d-sm-table-cell"></td>
                                        <td class="fs-sm text-muted">
                                            <a class="fw-semibold" href="be_pages_generic_profile.html">{{
                                                feedback.originator.first_name }} {{ feedback.originator.last_name
                                                }}</a>
                                            on <span class="text-muted">{{ formatDate(feedback.created_at) }}</span>
                                            <a class="btn btn-success btn-sm ms-2" target="_blank"
                                                :href="`https://api.whatsapp.com/send?text=${encodeURIComponent(feedback.content)}`">
                                                <i class="fa-brands fa-whatsapp"></i>
                                            </a>
                                            <span v-if="feedback.shipment_status_data" class="badge ms-2"
                                                :class="'bg-' + feedback.shipment_status_data.color + ' fw-bold text-uppercase p-2'">
                                                {{ feedback.shipment_status_data.title_ar }}
                                            </span>
                                        </td>
                                        <td>
                                            <button type="button" class="btn btn-danger btn-sm ms-2"
                                                @click="deleteFeedback(feedback._id, feedback.shipment_id)">
                                                <i class="fa-solid fa-trash"></i>
                                            </button>
                                        </td>
                                    </tr>
                                    <tr :class="{ 'border-bottom': index !== feedbacks.length - 1 }">
                                        <td class="d-none d-sm-table-cell text-center align-top" style="width: 140px">
                                            <a href="be_pages_generic_profile.html">
                                                <img class="img-avatar" :src="feedback.originator.avatar" alt="" />
                                            </a>
                                            <p class="fs-sm fw-medium text-muted">{{
                                                feedback.originator.role_title.role_title }}</p>
                                        </td>
                                        <td>
                                            <p>{{ feedback.content }}</p>
                                            <div class="gap-2 d-flex flex-wrap">
                                                <span v-if="feedback.is_unreachable"
                                                    class="fs-xs fw-semibold d-inline-block py-1 px-3 rounded-pill bg-danger-light text-danger">{{
                                                        $t("placeholder_is_unreachable") }}</span>
                                                <span v-if="feedback.is_evading"
                                                    class="fs-xs fw-semibold d-inline-block py-1 px-3 rounded-pill bg-warning-light text-warning">{{
                                                        $t("placeholder_is_evading") }}</span>
                                            </div>

                                        </td>
                                    </tr>
                                </template>
                            </tbody>
                        </table>
                    </div>

                    <!-- History Tab (Permission Check) -->
                    <div v-if="permissions.canManageShipment" class="tab-pane pull-x fs-sm" id="ecom-product-comments"
                        role="tabpanel" aria-labelledby="audit-log-tab" tabindex="0" style="padding: 20px;">
                        <meta name="csrf-token" :content="csrfToken" />
                        <ul id="logContainer" class="timeline-events timeline timeline-alt py-0">
                            <li v-for="(event, index) in timelineEvents" :key="index" class="timeline-event">
                                <div class="timeline-event-icon bg-primary">
                                    <i class="fa-solid fa-pen"></i>
                                </div>
                                <div class="timeline-event-block block">
                                    <div class="block-header d-flex justify-content-between">
                                        <h3 class="block-title">{{ $t('events.' + event.event) }}</h3>
                                        <div class="timeline-event-time block-options-item fs-sm">{{ event.created_at }}
                                        </div>
                                    </div>
                                    <div class="block-content">
                                        <table class="table table-striped">
                                            <thead>
                                                <tr>
                                                    <th style="width: 30%;">{{ $t('table_field') }}</th>
                                                    <th>{{ $t('table_old_value') }}</th>
                                                    <th>{{ $t('table_new_value') }}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr v-for="(row, rowIndex) in event.rows" :key="rowIndex">
                                                    <td>{{ row.field }}</td>
                                                    <td class="text-break">{{ row.oldValue }}</td>
                                                    <td class="text-break">{{ row.newValue }}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                        <p class="fw-semibold mb-2">
                                            {{ $t('table_changed_by') }}:
                                            <span class="fw-normal">{{ event.user }}</span>
                                        </p>
                                    </div>
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import ChangeStatusModal from './components/ChangeStatusModal.vue';
import ChangeCustodyModal from './components/ChangeCustodyModal.vue';
import swal from '../../../utils/swal';
export default {
    props: {
        initialShipment: Object,
        shipmentStatus: Array,
        grouped_users: Array,
        initialErrors: Array,
        permissions: Object,
        user: Object,
    },

    components: {
        ChangeStatusModal,
    },
    data() {
        return {
            shipment: { ...this.initialShipment }, // Create a copy of initialShipment
            errorMessages: this.initialErrors || [],
            csrfToken: document.querySelector('meta[name="csrf-token"]').content,
            isModalVisible: false, // Control modal visibility
            isLoading: false,
            feedbackTapLoading: false,
            logTapLoading: false,
            historyCount: 0,
            timelineEvents: [],
            feedbacks: [],
        }
    },

    mounted() {
        // Initialize any plugins
        console.log("Shipment Data:", this.shipment);
        $('.selectpicker').selectpicker();
        this.getShipmentFeedback();
        this.getShipmentLog();
    },

    methods: {
        async hasPermission(permission) {
            try {
                const response = await axios.get(`/partner-users/check-permission/${permission}`);
                return response.data.hasPermission;
            } catch (error) {
                console.error('Error checking permission:', error);
                return false;
            }
        },

        showChangeStatusModal() {
            this.$nextTick(() => {
                if (this.$refs.changeStatusModal) {
                    this.$refs.changeStatusModal.openModal();
                }
            });
        },

        handleStatusUpdate(updatedData) {
            console.log('Status updated:', updatedData);

            const updatedShipments = updatedData.data.shipments; // Assuming 'data.shipments' contains an array of updated shipments

            if (updatedShipments && updatedShipments.length > 0) {
                updatedShipments.forEach((updatedShipment) => {
                    // Check if the shipment is the one you want to update (match by ID or other unique identifier)
                    console.log('Updated Shipment from the details:', updatedShipment);

                    if (this.shipment._id === updatedShipment.id) {
                        // Update status if present in the response
                        if (updatedShipment.status) {
                            console.log('Updated Status:', updatedShipment.status);
                            this.shipment.status_id = updatedShipment.status.id; // Update the shipment's status ID
                            this.shipment.status_data = { ...updatedShipment.status }; // Optionally, store status data
                        }

                        // Update assigned user if present in the response
                        if (updatedShipment.assigned_user) {
                            console.log('Assigned User:', updatedShipment.assigned_user);
                            this.shipment.assigned_to_user = { ...updatedShipment.assigned_user }; // Update the assigned user
                        }

                        // You can also log or handle other properties if required
                    }
                });
            } else {
                console.warn('No updated shipments found in updated data.');
            }
        },

        printFullTag() {
            const shipmentId = this.initialShipment._id;
            const url = '/shipments/print/waybill';

            axios.post(url, {
                shipment_ids: [shipmentId]
            }, {
                headers: {
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
                },
                responseType: 'blob' // if the server returns a file (PDF/label)
            })
                .then(response => {
                    // If it's a file (like PDF), trigger browser download
                    const blob = new Blob([response.data], { type: response.headers['content-type'] });
                    const link = document.createElement('a');
                    link.href = window.URL.createObjectURL(blob);
                    link.download = `waybill-${shipmentId}.pdf`; // or any filename
                    link.click();
                })
                .catch(error => {
                    console.error('Error printing the shipment label:', error);
                });
        },

        printShipmentBarcode(shipmentId) {
            fetch('/shipments/print/barcode', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
                },
                body: JSON.stringify({
                    shipment_ids: [shipmentId]
                })
            })
                .then(response => {
                    if (response.ok) return response.blob();
                    throw new Error('Network response was not ok.');
                })
                .then(blob => {
                    const url = window.URL.createObjectURL(blob);
                    const iframe = document.createElement('iframe');
                    iframe.style.position = 'absolute';
                    iframe.style.width = '0';
                    iframe.style.height = '0';
                    iframe.style.border = 'none';
                    iframe.src = url;
                    document.body.appendChild(iframe);

                    iframe.onload = function () {
                        iframe.contentWindow.print();
                        document.body.removeChild(iframe); // Remove iframe after printing
                    };
                })
                .catch(error => console.error('Error:', error));
        },
        deleteShipment() {
            if (!this.initialShipment._id) {
                console.error('Shipment ID is missing!');
                return;
            }
            Swal.fire({
                title: "Are you sure?",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#899499",
                cancelButtonColor: "#d33",
                confirmButtonText: "Yes, delete it!"
            }).then((result) => {
                if (result.isConfirmed) {
                    axios.delete(
                        '/shipments/delete-shipment/' + this.initialShipment._id, // Corrected this line
                        {
                            headers: {
                                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
                            },
                        }
                    )
                        .then((response) => {
                            console.log(response.data);
                            swal.success(this.$t('message_shipment_deleted'));
                        })
                        .catch((error) => {
                            console.error(error);
                            swal.success(this.$t('message_general_error'));
                        });
                }
            });
        },


        // Restore shipment
        restoreShipment() {
            if (!this.initialShipment._id) {
                console.error('Shipment ID is missing!');
                return;
            }

            Swal.fire({
                title: "Are you sure?",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#899499",
                cancelButtonColor: "#d33",
                confirmButtonText: "Approve Restore"
            }).then((result) => {
                if (result.isConfirmed) {
                    axios.post('/shipments/restore-shipment/' + this.initialShipment._id,
                        {
                            headers: {
                                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
                            },
                        }
                    )
                        .then((response) => {
                            console.log(response.data);
                            swal.success(this.$t('message_shipment_restored'));
                        })
                        .catch((error) => {
                            console.error(error);
                            swal.error(this.$t('message_general_error'));
                        });
                }
            });
        },
        toggleButtonState() {
            this.isButtonDisabled = this.feedbackContent.length < 10;
        },
        formatDate(datetime) {
            return new Date(datetime).toLocaleString();
        },

        async getShipmentFeedback() {
            this.feedbackTapLoading = true;
            axios
                .get('/shipments/get-feedback', {
                    headers: {
                        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
                    },
                    params: {
                        shipment_id: this.initialShipment._id
                    }
                })
                .then((res) => {
                    this.feedbacks = res.data;
                })
                .catch((err) => {
                    console.error('Failed to load feedbacks:', err);
                })
                .finally(() => {
                    this.feedbackTapLoading = false;
                });
        },

        async postFeedback() {
            One.layout('header_loader_on');
            this.isButtonDisabled = true;

            try {
                const response = await axios.post('/shipments/post-feedback', {
                    shipment_id: this.initialShipment._id,
                    feedback_content: this.feedbackContent,
                    unreachable: this.unreachable,
                    evading: this.evading
                }, {
                    headers: {
                        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
                    }
                });

                // Success response handling
                this.feedbackContent = '';
                swal.success(this.$t('message_feedback_added'));
                this.getShipmentFeedback(); // Fetch feedback again after submitting
            } catch (error) {
                console.error('Failed to post feedback:', error);

                // Error handling
                if (error.response?.data?.errors) {
                    const messages = Object.values(error.response.data.errors).flat().join('\n');
                    this.showErrorNotification(messages); // Show errors returned from server
                } else {
                    // Fallback message for other errors
                    this.showErrorNotification('An error occurred while submitting feedback.');
                }

                // Show error notification if no specific error response
                swal.error(this.$t('message_general_error'));
            } finally {
                this.isButtonDisabled = false;
                One.layout('header_loader_off');
            }
        },

        deleteFeedback(feedbackId, shipmentId) {
            Swal.fire({
                title: "Are you sure?",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#899499",
                cancelButtonColor: "#d33",
                confirmButtonText: "Yes, delete it!"
            }).then((result) => {
                if (result.isConfirmed) {
                    axios.delete('/shipments/delete-feedback', {
                        data: {
                            feedback_id: feedbackId,
                            shipment_id: shipmentId
                        },
                        headers: {
                            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
                        },
                    })
                        .then((response) => {
                            console.log(response.data);
                            Swal.fire({
                                title: "Deleted!",
                                text: "The feedback has been deleted.",
                                icon: "success"
                            }).then(() => {
                                this.getShipmentFeedback(); // Refresh feedback list
                            });
                        })
                        .catch((error) => {
                            console.error(error);
                            swal.error(this.$t('message_general_error'));
                        });
                }
            });
        },

        async getShipmentLog() {
            this.logTapLoading = true;
            const shipmentId = this.initialShipment._id;

            try {
                const response = await axios.post(
                    '/shipments/get-shipment-log',
                    { shipment_id: shipmentId },
                    {
                        headers: {
                            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
                        }
                    }
                );

                const logs = response.data;
                this.historyCount = logs.length;

                this.timelineEvents = logs.map(value => {
                    const rows = [];

                    for (const field in value.old_values) {
                        rows.push({
                            field,
                            oldValue: value.old_values[field],
                            newValue: value.new_values[field] || ''
                        });
                    }

                    return {
                        event: value.event,
                        created_at: new Date(value.created_at).toLocaleString(),
                        user: value.user?.first_name + ' ' + value.user?.last_name || 'Deleted User',
                        rows
                    };
                });
            } catch (error) {
                console.error(error);
            } finally {
                this.logTapLoading = false;
            }
        },
        handleShipmentUpdate(updatedShipment) {
            console.log('Updated Shipment:', updatedShipment);
            // Update the local shipment data
            this.shipment = updatedShipment;
            this.getShipmentLog();
            // Optionally close the modal here
            this.isModalVisible = false;
        },
        showErrorNotification(message) {

            swal.error(this.$t('message_general_error'));
        }

    }


}
</script>

<style scoped>
/* Add your component-specific styles here */
</style>